import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { fetcher } from '@/utils/fetcher';
import { useConfigurations } from '@/features/_data/useConfigurations';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { useUnitData } from '@/features/_data/useUnitData';
import { Modal } from '@/ui/Modal/Modal';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  isModalOpen: boolean;
  unitId: string | undefined;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DeleteConfigurationModal = ({
  isModalOpen,
  setIsModalOpen,
  unitId,
}: Props) => {
  const [selectedCustomerId] = useSelectedCustomerId();
  const [isDeleting, setIsDeleting] = useState(false);
  const { refetch: refetchConfigurations } =
    useConfigurations(selectedCustomerId);

  const toast = useToast();
  const intl = useIntl();
  const { refetch: refetchUnitData } = useUnitData(unitId);
  const queryClient = useQueryClient();

  const handleDeleteUnit = async () => {
    if (!navigator.onLine) {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage:
            'Please connect to the internet to delete the Configuration.',
          id: 'EHQMVp',
        }),
        mode: TOASTTYPE.No_Connection,
      });
      return;
    }

    try {
      setIsDeleting(true);
      if (typeof unitId === 'undefined') return;
      const response = await fetcher<{ success: boolean }>(
        `${RETAIL_API_URL}/units/delete?id=${unitId}`,
        {
          method: 'POST',
        },
      );
      if (response.success) {
        queryClient.removeQueries({ queryKey: ['unit', unitId] });
        queryClient.invalidateQueries({ queryKey: ['units'] });
      }
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'The configuration has been deleted!',
          id: 'v+ZJxv',
        }),
        mode: TOASTTYPE.Success,
      });
      setIsDeleting(false);
      setIsModalOpen(false);
      await refetchConfigurations();
      await refetchUnitData();
    } catch (error) {
      toast({
        title: intl.formatMessage({
          defaultMessage:
            'Error! Something went wrong while deleting the Configuration.',
          id: 'RTxfZe',
        }),
        mode: TOASTTYPE.Danger,
      });

      setIsModalOpen(false);
    }
  };

  return (
    isModalOpen && (
      <Modal
        title="Configuration will be deleted"
        footer={
          <Flex gap={16} grow justify="flex-end" padding={24}>
            <Button
              mode="link"
              onClick={() => {
                setIsModalOpen(false);
              }}
              size="large"
            >
              <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
            </Button>
            <Button
              mode="danger"
              onClick={() => {
                handleDeleteUnit();
              }}
              loading={isDeleting}
              size="large"
            >
              <FormattedMessage defaultMessage="Delete" id="K3r6DQ" />
            </Button>
          </Flex>
        }
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        Are you sure you want to delete configuration?
      </Modal>
    )
  );
};
